import React, { useEffect, useId, useRef, useState } from 'react'
import Dialog from '@mui/material/Dialog'
import Button from '../Button/Button'
import { UiKitContext, useUiKit } from '../UiKit/UiKitContext'
import { normalizeError } from '../../utils/errorHelpers'
import TypedCheck, { typedMatches } from './TypedCheck'

const ConfirmDialog = ({
  open,
  onClose,
  title,
  impact,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  tone = 'primary',
  typedText,
  typedLabel,
  confirmDisabled = false,
  onConfirm,
}) => {
  const kit = useUiKit()
  const titleId = useId()
  const typedId = useId()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [typed, setTyped] = useState('')
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  const shown = useRef({ title, impact, confirmLabel, children, tone, typedText, typedLabel })
  if (open) shown.current = { title, impact, confirmLabel, children, tone, typedText, typedLabel }

  useEffect(() => {
    if (!open) return
    setTyped('')
    setError(null)
  }, [open])

  const view = shown.current
  const hasTypedCheck = view.typedText !== undefined && view.typedText !== null && `${view.typedText}` !== ''
  const typedOk = !hasTypedCheck || typedMatches(typed, view.typedText)

  const close = () => {
    if (!busy) onClose()
  }

  const confirm = async () => {
    if (busy || !typedOk || confirmDisabled) return
    setBusy(true)
    setError(null)
    try {
      await onConfirm()
    } catch (failure) {
      if (mounted.current) {
        setBusy(false)
        setError(normalizeError(failure?.message || failure))
      }
      return
    }
    if (mounted.current) setBusy(false)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      disableEscapeKeyDown={busy}
      aria-labelledby={titleId}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', backgroundImage: 'none' } }}
    >
      <UiKitContext.Provider value={{ ...kit, button: true }}>
        <div className="p-6 flex flex-col gap-4 bg-paper">
          <h2 id={titleId} className="font-sans text-lg-18 font-semibold text-text-primary">{view.title}</h2>
          {Array.isArray(view.impact) && view.impact.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-text-primary mb-1">What will happen</p>
              <ul className="list-disc pl-5 text-sm text-text-secondary space-y-1">
                {view.impact.map((line) => <li key={line}>{line}</li>)}
              </ul>
            </div>
          )}
          {view.children}
          {hasTypedCheck && (
            <TypedCheck
              id={typedId}
              label={view.typedLabel || `Type ${view.typedText} to confirm`}
              value={typed}
              onChange={setTyped}
              disabled={busy}
            />
          )}
          {error && <p role="alert" className="text-sm text-error-main">{error}</p>}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="outlined" size="md" htmlType="button" onClick={close} disabled={busy} autoFocus>
              {cancelLabel}
            </Button>
            <Button
              size="md"
              htmlType="button"
              variant={view.tone === 'danger' ? 'danger' : undefined}
              loading={busy}
              disabled={!typedOk || confirmDisabled}
              onClick={confirm}
            >
              {view.confirmLabel}
            </Button>
          </div>
        </div>
      </UiKitContext.Provider>
    </Dialog>
  )
}

export default ConfirmDialog
